import { Injectable, ComponentFactoryResolver, Injector, ApplicationRef } from "@angular/core";
import * as L from "leaflet";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { ICoolersDetails } from "../Models/ICoolersDetails";
import { label } from "../constants/labels";
import { I18nService } from "@app/core/i18n.service";

const initialZoom = 9;
const maxZoom = 18;
const shadowUrl = "assets/marker-shadow.png";
@Injectable()
export class MapService {
  public _map: any | null;
  private _pendingCoolers: ICoolersDetails[] | null = null;
  private _markerLayer: L.LayerGroup | null = null;
  labels: any;
  lang: string;
  public injector: Injector;
  public appRef: ApplicationRef;
  public resolver: ComponentFactoryResolver;
  public compRef: any;
  public component: any;
  public markerShow: any;
  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly translate1: TranslateService,
    private readonly i18Service: I18nService
  ) {}

  public initMap(mapid: string) {
    
    this.labels = label;
    this.lang = this.i18Service.language;
    if (this._map) {
      this._map.remove();
    }
    this._map = new L.Map(mapid, { attributionControl: false });
    this._markerLayer = new L.LayerGroup().addTo(this._map);
    // switch off unneeded features for better performance
    this._map.addLayer(
      new L.TileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: maxZoom,
        minZoom: 1,
        maxNativeZoom: 19,
        noWrap: false,
        updateWhenIdle: true,
      })
    );

    // Add any pending markers that were queued before the map was ready
    if (this._pendingCoolers && this._pendingCoolers.length > 0) {
      this.addCoolersMarker(this._pendingCoolers);
      this._pendingCoolers = null;
    }
  }

  get selectedLocation(): any {
    return JSON.parse(sessionStorage.getItem("location"));
  }

  // Create custom marker with device image inside polygon shape
  private createDeviceIcon(severity: number, deviceImageUrl: string = "assets/AIR Edge 1.png"): L.DivIcon {
    let polygonImage: string;
    switch (severity) {
      case 1:
        polygonImage = "assets/Polygon_red.png"; // Red for high severity
        break;
      case 2:
        polygonImage = "assets/Polygon_yellow.png"; // Yellow for medium severity
        break;
      default:
        polygonImage = "assets/Polygon_grey.png"; // Grey for low/no severity
        break;
    }

    const iconHtml = `
      <div class="device-marker-container">
        <img class="device-marker-polygon" src="${polygonImage}" alt="Marker" />
        <img class="device-marker-image" src="assets/AIR Edge 1.png" alt="Device" width="10px" height: />
      </div>
    `;

    return L.divIcon({
      className: "device-custom-marker",
      html: iconHtml,
      iconSize: [56, 64],
      iconAnchor: [28, 64],
      popupAnchor: [0, -64],
    });
  }

  //Map Icons (kept for backward compatibility)
  private readonly inactiveIcon: L.Icon = new L.Icon({
    iconUrl: "assets/Polygon_grey.png",
    shadowUrl: shadowUrl,
    iconSize: [28, 32],
    iconAnchor: [12.5, 41],
  });

  private readonly lowSeverityIcon: L.Icon = new L.Icon({
    iconUrl: "assets/Polygon_grey.png",
    shadowUrl: shadowUrl,
    iconSize: [28, 32],
    iconAnchor: [12.5, 41],
  });

  private readonly medSeverityIcon: L.Icon = new L.Icon({
    iconUrl: "assets/Polygon_yellow.png",
    shadowUrl: shadowUrl,
    iconSize: [28, 32],
    iconAnchor: [12.5, 41],
  });

  private readonly highSeveritIcon: L.Icon = new L.Icon({
    iconUrl: "assets/Polygon_red.png",
    shadowUrl: shadowUrl,
    iconSize: [28, 32],
    iconAnchor: [12.5, 41],
  });

  //Icon based on status
  private getIcon(severity: number) {
    let icon: L.Icon;
    const s: number = severity;
    switch (s) {
      case 1: {
        icon = this.highSeveritIcon;
        break;
      }
      case 2: {
        icon = this.medSeverityIcon;
        break;
      }

      default: {
        icon = this.lowSeverityIcon;
        break;
      }
    }
    return icon;
  }

  addCoolersMarker(coolers: ICoolersDetails[]) {
    if (!coolers || coolers.length === 0) {
      if (this._markerLayer) {
        this._markerLayer.clearLayers();
      }
      return;
    }
    
    // If map is not ready yet, store the coolers and add them when map initializes
    if (!this._map) {
      this._pendingCoolers = coolers;
      return;
    }

    // Clear existing markers before adding new ones
    if (this._markerLayer) {
      this._markerLayer.clearLayers();
    }
    
    for (const cooler of coolers) {
      const deviceImage = cooler.coolerImage || "assets/AIR Edge 1.png";
      const marker = new L.Marker([cooler.latitude, cooler.longitude], {
        icon: this.createDeviceIcon(cooler.alarm_Severity, deviceImage),
        riseOnHover: true,
      });
      // Add permanent tooltip above marker to show device name
      marker.bindTooltip(cooler.name, {
        permanent: true,
        direction: "top",
        offset: L.point(0, -60),
        className: "device-name-tooltip",
      });
      marker
        .bindPopup(
          `<div class="map-popup-container ${cooler.alarm_Severity === 1 ? 'critical-alarm' : ''}">
    <div class="map-popup-content">
        <div class="map-popup-row">
            <div class="map-popup-image-col">
                <img class="map-popup-image" src="${deviceImage}" />
            </div>
            <div class="map-popup-details">
                <div class="map-popup-info">
                    <div class="map-popup-title">
                        <div class="map-popup-title-text">Device Details</div>
                    </div>
                    <div class="map-popup-field">
                        <div class="map-popup-field-content">
                            <div class="map-popup-label">Edge Device</div>
                            <div class="map-popup-value">${this.translate1.instant(
                              this.labels.tableHeaderDevice
                            )}: ${cooler.name}</div>
                            <div class="map-popup-subtitle"></div>
                        </div>
                    </div>
                    <div class="map-popup-owner-field">
                        <div class="map-popup-owner-content">
                            <div class="map-popup-owner-text">Owner Name: ${cooler.ownerName}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>`
        )
        .openPopup();
      marker.addTo(this._markerLayer);
    }
    if (coolers) {
      this._map.setView([coolers[0].latitude, coolers[0].longitude], initialZoom);
    }
  }

  locate(latitude: number, longitude: number, cooler: any) {
    if (!this._map || !cooler) {
      return;
    }
    const deviceImage = cooler.coolerImage || "assets/AIR Edge 1.png";
    // Create normal and hover icons with different class names
    const normalIcon = L.divIcon({
      className: "device-custom-marker",
      html: `
        <div class="device-marker-container">
          <img class="device-marker-polygon" src="${cooler.alarm_Severity === 1 ? 'assets/Polygon_red.png' : cooler.alarm_Severity === 2 ? 'assets/Polygon_yellow.png' : 'assets/Polygon_grey.png'}" alt="Marker" />
          <img class="device-marker-image" src="assets/AIR Edge 1.png" alt="Device" width="0.8vh" />
        </div>
      `,
      iconSize: [56, 64],
      iconAnchor: [28, 64],
      popupAnchor: [0, -64],
    });
    const hoverIcon = L.divIcon({
      className: "device-custom-marker",
      html: `
        <div class="device-marker-container device-marker-hover">
          <img class="device-marker-polygon" src="${cooler.alarm_Severity === 1 ? 'assets/Polygon_red.png' : cooler.alarm_Severity === 2 ? 'assets/Polygon_yellow.png' : 'assets/Polygon_grey.png'}" alt="Marker" />
          <img class="device-marker-image" src="assets/AIR Edge 1.png" alt="Device" width="0.8vh" />
        </div>
      `,
      iconSize: [56, 64],
      iconAnchor: [28, 64],
      popupAnchor: [0, -64],
    });

    // Show hover effect immediately when located
    const marker = new L.Marker([cooler.latitude, cooler.longitude], {
      icon: hoverIcon,
      riseOnHover: true,
    });
    // Add permanent tooltip above marker to show device name
    marker.bindTooltip(cooler.name, {
      permanent: true,
      direction: "top",
      offset: L.point(0, -60),
      className: "device-name-tooltip",
    });
    marker
      .bindPopup(
        `<div class="map-popup-container ${cooler.alarm_Severity === 1 ? 'critical-alarm' : ''}">
          <div class="map-popup-content">
              <div class="map-popup-row">
                  <div class="map-popup-image-col">
                      <img class="device-marker-popup" src="${deviceImage}" />
                  </div>
                  <div class="map-popup-details">
                      <div class="map-popup-info">
                          <div class="map-popup-title">
                              <div class="map-popup-title-text">Device Details</div>
                          </div>
                          <div class="map-popup-field">
                              <div class="map-popup-field-content">
                                  <div class="map-popup-label">Edge Device</div>
                                  <div class="map-popup-value">${this.translate1.instant(
                                    this.labels.tableHeaderDevice 
                                  )}: ${cooler.name}</div>
                                  <div class="map-popup-subtitle"></div>
                              </div>
                          </div>
                          <div class="map-popup-owner-field">
                              <div class="map-popup-owner-content">
                                  <div class="map-popup-owner-text">Owner Name: ${cooler.ownerName}</div>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
        </div>`
      )
      .openPopup();
    marker.addTo(this._map);
    marker.togglePopup();

    // Add hover effect: keep hoverIcon while mouse is over, revert to normalIcon on mouseout
    marker.on('mouseover', () => {
      marker.setIcon(hoverIcon);
    });
    marker.on('mouseout', () => {
      marker.setIcon(normalIcon);
    });
  }

  closePopup() {
    if (this._map) {
      this._map.closePopup();
    }
  }

  public destroyMap() {
    if (this._markerLayer) {
      this._markerLayer.clearLayers();
      this._markerLayer = null;
    }
    if (this._map) {
      this._map.remove();
      this._map = null;
    }
  }
}
